import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMobileNav } from "@/contexts/MobileNavContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import Step1Upload from "./Step1Upload";
import Step2Analyze from "./Step2Analyze";
import { isAIDisabled } from "@/lib/smartfile-ai-preference";

export default function SmartfileWizard() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setShowMobileNav } = useMobileNav();
  const patientId = searchParams.get("patientId");
  const sessionIdFromUrl = searchParams.get("sessionId");
  const fromAssisted = searchParams.get("fromAssisted") === "true";

  const [currentStep, setCurrentStep] = useState(sessionIdFromUrl || fromAssisted ? 2 : 1);
  const [sessionId, setSessionId] = useState<string | null>(sessionIdFromUrl);
  const [contentType, setContentType] = useState<string>('other');
  const [isReanalyzing, setIsReanalyzing] = useState(false);

  useEffect(() => {
    setShowMobileNav(false);
    return () => setShowMobileNav(true);
  }, [setShowMobileNav]);

  const loadContentType = useCallback(async () => {
    if (!sessionId) return;
    
    const { data, error } = await supabase
      .from('smartfile_sessions')
      .select('content_type')
      .eq('id', sessionId)
      .single();

    if (!error && data) {
      setContentType(data.content_type || 'other');
    }
  }, [sessionId]);

  // Carregar content_type quando estiver no step 2
  useEffect(() => {
    if (currentStep === 2 && sessionId) {
      loadContentType();
    }
  }, [currentStep, sessionId, loadContentType]);

  const handleContentTypeChange = async (newType: string) => {
    if (!sessionId) return;
    
    try {
      const { error } = await supabase
        .from('smartfile_sessions')
        .update({ content_type: newType, updated_at: new Date().toISOString() })
        .eq('id', sessionId);

      if (error) throw error;
      
      setContentType(newType);
      toast.success('Tipo de documento atualizado!');
      
      // Trigger reload no Step2Analyze
      window.dispatchEvent(new CustomEvent('smartfile-reload'));
    } catch (error) {
      console.error('Error updating content type:', error);
      toast.error('Erro ao atualizar tipo');
    }
  };

  const handleReanalyze = async () => {
    if (!sessionId) return;
    
    setIsReanalyzing(true);
    try {
      const { data: sessionData } = await supabase
        .from('smartfile_sessions')
        .select('extracted_text')
        .eq('id', sessionId)
        .single();

      if (!sessionData?.extracted_text) {
        toast.error('Texto extraído não encontrado');
        return;
      }

      const { data, error } = await supabase.functions.invoke('smartfile-v15-structure-records-fast', {
        body: {
          extractedText: sessionData.extracted_text,
          contentType: contentType
        }
      });

      if (error) throw error;

      const { error: updateError } = await supabase
        .from('smartfile_sessions')
        .update({ structured_records: data.records })
        .eq('id', sessionId);

      if (updateError) throw updateError;

      toast.success('Registros reanalisados com sucesso!');
      
      // Trigger reload no Step2Analyze
      window.dispatchEvent(new CustomEvent('smartfile-reload'));
    } catch (error) {
      console.error('Error reanalyzing:', error);
      toast.error('Erro ao reanalisar documento');
    } finally {
      setIsReanalyzing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Steps */}
      {currentStep === 1 && (
        <Step1Upload
          patientId={patientId}
          onComplete={(newSessionId) => {
            setSessionId(newSessionId);
            setCurrentStep(2);
          }}
          disableAI={isAIDisabled()}
        />
      )}

      {currentStep === 2 && sessionId && (
        <Step2Analyze
          sessionId={sessionId}
          fromAssisted={fromAssisted}
          onComplete={async () => {
            // Fetch patient_id from session to ensure correct redirection
            const { data: sessionData } = await supabase
              .from('smartfile_sessions')
              .select('patient_id')
              .eq('id', sessionId)
              .single();
            
            const finalPatientId = sessionData?.patient_id || patientId;
            
            if (finalPatientId) {
              navigate(`/app/pacientes/${finalPatientId}`);
            } else {
              navigate("/app/pacientes");
            }
          }}
          onBack={() => setCurrentStep(1)}
        />
      )}
    </div>
  );
}
